import type { FieldDef } from '@/modules/catalog'
import { formatDateTime } from '@/shared/dateTime'

export const replacementBase='/api/v1/sales/after-sale-replacements'
export const replacementReadPermissions=['sales.view_salesaftersale','sales.view_salesoutbound'] as const
export const replacementMakePermissions=[...replacementReadPermissions,'sales.make_salesaftersalereplacement',
  'sales.add_salesorder','sales.view_salesorder','masterdata.view_product_amount'] as const
export const replacementDirectMakePermissions=[...replacementReadPermissions,'sales.make_salesaftersalereplacement',
  'sales.add_salesoutbound','masterdata.view_product_amount'] as const
export const replacementCostPermissions=[...replacementReadPermissions,'sales.request_salescommercialrelease',
  'sales.view_salescommercialrelease'] as const
export const replacementExecutePermissions=[...replacementReadPermissions,'sales.execute_salesaftersalereplacement',
  'inventory.view_inventorybatch'] as const
export type ReplacementAction='create-order'|'prepare-direct'|'request-cost'|'execute-direct'|'void-direct'
type ProductLabel={sku:string;product_name:string;product_name_en:string;unit:string;unit_type?:string}
export type ReplacementLine=ProductLabel&{claim_line_id?:number;quantity:string;remaining_quantity?:string}
export type ReplacementDispatchLine=ProductLabel&{id:number;claim_line_id:number;quantity:string;planned_quantity:string}
export type ReplacementDoc={id:number;doc_no:string;status:string}
export type ReplacementDispatch=ReplacementDoc&{lines:ReplacementDispatchLine[];delivery?:ReplacementDoc|null;cost_amount?:string}
export type ReplacementCost=ReplacementDoc&{version:number;approval_id:number|null;outbound_id:number;cost?:string}
export type ReplacementOption={id:number;doc_no:string;version:number;outbound_no:string;customer:string;warehouse:string;entry_mode?:'order'|'simple'}
export type ReplacementContext=ReplacementOption&{lines:ReplacementLine[];
  addresses:{id:number;code:string;label:string;is_default:boolean}[];already_linked:boolean;order:ReplacementDoc|null;can_create:boolean;
  direct_draft?:ReplacementDispatch|null;dispatches?:ReplacementDispatch[];cost_reviews?:ReplacementCost[];
  has_approved_cost?:boolean;cost_approval_pending?:boolean;
  selected_address_id?:number|null;can_prepare_direct?:boolean;can_request_cost?:boolean;can_execute_direct?:boolean;can_void_direct?:boolean}
export type ReplacementPending={claim_id:number;path:string;key:string;body:string}
export const replacementStorageKey=(actor:number)=>`coldwise:aftersale-replacement:${actor}`
const positiveId=(value:unknown):value is number=>typeof value==='number'&&Number.isSafeInteger(value)&&value>0
const plain=(value:unknown):value is Record<string,unknown>=>Boolean(value)&&typeof value==='object'&&!Array.isArray(value)
function exactKeys(value:Record<string,unknown>,keys:string[]){return Object.keys(value).sort().join(',')===[...keys].sort().join(',')}
function scaled(value:string){
  if(!/^(0|[1-9]\d{0,14})(\.\d{1,3})?$/.test(value))throw new Error('quantity')
  const[whole,fraction='']=value.split('.');return BigInt(whole!)*1000n+BigInt(fraction.padEnd(3,'0'))
}
export function replacementQuantityEqual(left:unknown,right:unknown){
  if(typeof left!=='string'||typeof right!=='string')return false
  try{return scaled(left)===scaled(right)}catch{return false}
}
function quantity(value:string,limit:string,unitType?:string,allowZero=false){
  const parsed=scaled(value)
  if(parsed>scaled(limit)||(!allowZero&&parsed===0n)||(['package','count'].includes(unitType??'')&&parsed%1000n!==0n))throw new Error('quantity')
  return value
}
export function replacementQuantityField(unitType?:string,allowZero=false):FieldDef{
  const integer=['package','count'].includes(unitType??'')
  return{key:'replacement_quantity',kind:integer?'integer':'decimal',label:'数量',labelEn:'Quantity',
    numeric:{precision:integer?0:3,maxDigits:18,min:allowZero?'0':integer?'1':'0.001',
      max:integer?'999999999999999':'999999999999999.999'}}
}
export function editableReplacementQuantity(value:string,unitType?:string){
  return ['package','count'].includes(unitType??'')?value.replace(/\.0+$/,''):value
}
function addressValue(context:ReplacementContext,address:number|undefined){
  if(address!==undefined&&!context.addresses.some(row=>row.id===address))throw new Error('source')
  return address??null
}
function note(reason:string,fallback?:string){
  if(reason.length>2000||(!reason.trim()&&!fallback))throw new Error('reason')
  return reason.trim()||fallback!
}
export function replacementPayload(context:ReplacementContext,reason:string,address:number|undefined){
  return{expected_version:context.version,reason:note(reason),shipping_address_id:addressValue(context,address)}
}
export function replacementPreparePayload(context:ReplacementContext,reason:string,address:number|undefined,quantities:Record<number,string>){
  const lines=context.lines.flatMap(row=>{
    if(!positiveId(row.claim_line_id))throw new Error('source')
    const value=quantities[row.claim_line_id]??row.remaining_quantity??row.quantity
    quantity(value,row.remaining_quantity??row.quantity,row.unit_type,true)
    return scaled(value)===0n?[]:[{claim_line_id:row.claim_line_id,quantity:value}]
  })
  if(!lines.length)throw new Error('quantity')
  return{expected_version:context.version,reason:note(reason,'按已批准余量准备本次补寄 / Prepare this approved replacement'),
    shipping_address_id:addressValue(context,address),lines}
}
export function replacementCostPayload(context:ReplacementContext,reason:string){
  if(!context.direct_draft)throw new Error('source')
  return{expected_version:context.version,outbound_id:context.direct_draft.id,
    reason:note(reason,'核对本次补寄计划并申请独立成本复核 / Request independent replacement cost review')}
}
export function replacementExecutePayload(context:ReplacementContext,reason:string,address:number|undefined,
  quantities:Record<number,string>,confirmed:boolean,receivedAt=''){
  if(!confirmed||!context.direct_draft)throw new Error('actual')
  const occurred_at=receivedAt?`${receivedAt.replace(' ','T')}+08:00`:new Date().toISOString()
  if(receivedAt&&(!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(receivedAt)||formatDateTime(occurred_at)!==receivedAt))throw new Error('actual')
  const lines=context.direct_draft.lines.map(row=>({outbound_line_id:row.id,
    quantity:quantity(quantities[row.id]??row.quantity,row.planned_quantity,row.unit_type)}))
  if(!lines.length)throw new Error('actual')
  return{expected_version:context.version,outbound_id:context.direct_draft.id,occurred_at,physical_dispatch_confirmed:true,
    reason:note(reason,'核对批准额度后确认本次补寄实物已发出 / Confirm actual approved replacement dispatch'),
    shipping_address_id:addressValue(context,address),lines}
}
export function replacementVoidPayload(context:ReplacementContext,reason:string){
  if(!context.direct_draft)throw new Error('source')
  return{expected_version:context.version,outbound_id:context.direct_draft.id,reason:note(reason)}
}
export function replacementAction(command:ReplacementPending):ReplacementAction{
  return command.path.slice(command.path.lastIndexOf('/')+1) as ReplacementAction
}
function validLines(value:unknown,idKey:string){
  if(!Array.isArray(value)||!value.length||value.length>100)return false
  const seen=new Set<number>()
  return value.every(row=>{
    if(!plain(row)||!exactKeys(row,[idKey,'quantity'])||!positiveId(row[idKey])||typeof row.quantity!=='string'||seen.has(row[idKey]))return false
    seen.add(row[idKey]);try{return scaled(row.quantity)>0n}catch{return false}
  })
}
function validInstant(value:unknown){
  if(typeof value!=='string'||!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|\+08:00)$/.test(value)||!Number.isFinite(Date.parse(value)))return false
  return value.endsWith('Z')?new Date(value).toISOString().slice(0,19)===value.slice(0,19)
    :formatDateTime(value)===value.slice(0,19).replace('T',' ')
}
export function parseReplacementPending(raw:string):ReplacementPending{
  const value=JSON.parse(raw) as ReplacementPending
  if(!plain(value)||!exactKeys(value,['body','claim_id','key','path'])||!positiveId(value.claim_id)
    ||typeof value.path!=='string'||typeof value.key!=='string'||!/^[\da-f]{8}(-[\da-f]{4}){3}-[\da-f]{12}$/i.test(value.key)
    ||typeof value.body!=='string'||value.body.length>10000)throw new Error('pending')
  const action=replacementAction(value)
  if(!['create-order','prepare-direct','request-cost','execute-direct','void-direct'].includes(action)
    ||value.path!==`${replacementBase}/${value.claim_id}/${action}`)throw new Error('pending')
  const body:unknown=JSON.parse(value.body)
  if(!plain(body)||!positiveId(body.expected_version)||typeof body.reason!=='string'||!body.reason.trim()||body.reason.length>2000)throw new Error('body')
  const addressed=body.shipping_address_id===null||positiveId(body.shipping_address_id)
  if(action==='create-order'&&(!exactKeys(body,['expected_version','reason','shipping_address_id'])||!addressed))throw new Error('body')
  if(action==='prepare-direct'&&(!exactKeys(body,['expected_version','reason','shipping_address_id','lines'])||!addressed||!validLines(body.lines,'claim_line_id')))throw new Error('body')
  if(action==='request-cost'&&(!exactKeys(body,['expected_version','outbound_id','reason'])||!positiveId(body.outbound_id)))throw new Error('body')
  if(action==='void-direct'&&(!exactKeys(body,['expected_version','outbound_id','reason'])||!positiveId(body.outbound_id)))throw new Error('body')
  if(action==='execute-direct'&&(!exactKeys(body,['expected_version','outbound_id','occurred_at','physical_dispatch_confirmed','reason','shipping_address_id','lines'])
    ||!positiveId(body.outbound_id)||body.physical_dispatch_confirmed!==true||!validInstant(body.occurred_at)||!addressed||!validLines(body.lines,'outbound_line_id')))throw new Error('body')
  return value
}
